<!-- Labs_History.vue — graph-sourced -->
<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200">
    <!-- Header -->
    <div class="px-4 py-2.5 border-b border-gray-200">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <h3 class="text-base font-semibold text-gray-900">Labs</h3>
          <span v-if="labs.length" class="text-xs text-gray-400">({{ filteredLabs.length }})</span>
        </div>
        <button
          @click="$emit('refresh')"
          :disabled="loading"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <FontAwesomeIcon
            :icon="['fas', 'sync']"
            :class="{ 'animate-spin': loading }"
            class="text-sm"
          />
        </button>
      </div>
    </div>

    <!-- AI Summary Banner -->
    <div v-if="summary" class="px-4 py-3 bg-amber-50 border-b border-amber-100">
      <div class="flex items-start gap-2">
        <FontAwesomeIcon :icon="['fas', 'wand-magic-sparkles']" class="text-[10px] text-amber-500 mt-1 flex-shrink-0" />
        <p class="text-xs text-amber-800 leading-relaxed" data-demo-blur>{{ summary }}</p>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="flex items-center justify-center h-48">
      <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-xl text-gray-400 animate-spin" />
    </div>

    <!-- Empty -->
    <div v-else-if="labs.length === 0" class="text-center py-8 px-4">
      <FontAwesomeIcon :icon="['fas', 'flask']" class="text-2xl text-gray-300 mb-2" />
      <p class="text-sm text-gray-500">No lab results found</p>
    </div>

    <div v-else class="p-4">
      <!-- Search & Source Filter -->
      <div class="flex items-center gap-2 mb-3 flex-wrap">
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Search test name or LOINC..."
          class="flex-1 min-w-[180px] px-2.5 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent"
        />
        <select
          v-if="sourceOptions.length > 1"
          v-model="sourceFilter"
          class="px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white text-gray-700"
        >
          <option value="">All sources</option>
          <option v-for="src in sourceOptions" :key="src" :value="src">{{ src }}</option>
        </select>
        <button
          type="button"
          @click="abnormalOnly = !abnormalOnly"
          :class="[
            'px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors',
            abnormalOnly ? 'bg-red-50 border-red-200 text-red-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
          ]"
        >Abnormal only</button>
      </div>

      <!-- Standardized Labs Table -->
      <div class="overflow-x-auto">
        <table class="w-full text-xs border-collapse">
          <thead>
            <tr class="border-b border-gray-200 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              <th class="py-1.5 pr-3">Test</th>
              <th class="py-1.5 px-2">Result</th>
              <th class="py-1.5 px-2">Ref Range</th>
              <th class="py-1.5 px-2">Source</th>
              <th class="py-1.5 pl-2 text-right">Date</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-50">
            <tr
              v-for="(lab, i) in filteredLabs"
              :key="i"
              class="hover:bg-gray-50/60"
            >
              <td class="py-2 pr-3 max-w-[220px]">
                <div class="font-medium text-gray-900 truncate" :title="lab.displayName">
                  {{ lab.displayName }}
                </div>
                <div v-if="lab.codeSubtitle" class="text-[10px] text-gray-400 truncate">
                  {{ lab.codeSubtitle }}
                </div>
              </td>
              <td class="py-2 px-2 whitespace-nowrap">
                <span class="font-semibold" :class="lab.isAbnormal ? 'text-red-600' : 'text-gray-900'">
                  {{ lab.displayValue }}
                </span>
                <span v-if="lab.displayUnits" class="text-gray-400 font-normal ml-0.5">{{ lab.displayUnits }}</span>
                <span
                  v-if="lab.abnormalBadge"
                  class="ml-1 inline-flex items-center px-1 py-0.5 rounded text-[9px] font-semibold bg-red-50 text-red-700"
                >{{ lab.abnormalBadge }}</span>
              </td>
              <td class="py-2 px-2 text-gray-500 whitespace-nowrap">
                {{ lab.displayRange || '—' }}
              </td>
              <td class="py-2 px-2 whitespace-nowrap">
                <span v-if="lab.sourceLabel" class="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[10px] font-medium">
                  {{ lab.sourceLabel }}
                </span>
                <span v-else class="text-gray-300">—</span>
              </td>
              <td class="py-2 pl-2 text-right text-[11px] text-gray-500 whitespace-nowrap">
                {{ formatDate(lab.SERVICE_DATE) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { formatCalendarDate } from '@/utils/common';

const SOURCE_LABELS = {
  quest: 'Quest',
  labcorp: 'LabCorp',
  athena: 'Athena',
  healthjump: 'HealthJump',
  ellkay: 'Ellkay',
  aetna: 'Aetna',
};

const ABNORMAL_CODES = new Set([
  'A', 'AA', 'H', 'HH', 'HI', 'HIGH', 'L', 'LL', 'LO', 'LOW',
  'POS', 'POSITIVE', 'ABNORMAL', 'ABN', 'CRIT', 'CRITICAL', 'PANIC',
]);

function normalizeTestName(rawName, rawCode) {
  let s = String(rawName || '').trim();
  s = s.replace(/^[#*]+\s*/, '').replace(/^#\d+\s+/, '').replace(/\s+/g, ' ').trim();
  if (!s && rawCode) s = String(rawCode).trim();
  return s;
}

function normalizeUnits(rawUnits) {
  const u = String(rawUnits || '').trim();
  if (!u || u === '-' || u === 'NULL' || u === 'N/A') return '';
  return u;
}

function normalizeRange(rawRange) {
  const r = String(rawRange || '').replace(/\s+/g, ' ').trim();
  if (!r || /^[-–\s]+$/.test(r) || r === 'NULL' || r === 'N/A') return '';
  return r;
}

function classifyAbnormal(flag) {
  const f = String(flag || '').trim().toUpperCase();
  if (!f || f === 'N' || f === 'NORMAL' || f === 'NEG' || f === 'NEGATIVE' || f === 'NONE') {
    return { isAbnormal: false, badge: '' };
  }
  if (ABNORMAL_CODES.has(f)) {
    const label = ['H', 'HH', 'HI', 'HIGH'].includes(f) ? 'High'
      : ['L', 'LL', 'LO', 'LOW'].includes(f) ? 'Low'
      : ['POS', 'POSITIVE'].includes(f) ? 'Pos'
      : 'Abn';
    return { isAbnormal: true, badge: label };
  }
  return { isAbnormal: false, badge: '' };
}

export default {
  name: 'Labs_History',
  components: { FontAwesomeIcon },
  props: {
    observations: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    summary: { type: String, default: '' }
  },
  emits: ['refresh'],
  data() {
    return {
      searchQuery: '',
      sourceFilter: '',
      abnormalOnly: false,
    };
  },
  computed: {
    labs() {
      return (this.observations || [])
        .filter(o => {
          const cat = (o.CATEGORY || '').toLowerCase();
          return cat === 'laboratory' || cat === 'lab';
        })
        .map(o => {
          const displayName = normalizeTestName(o.CODE_DISPLAY, o.CODE);
          const rawVal = o.VALUE_STRING != null && String(o.VALUE_STRING).trim() !== ''
            ? String(o.VALUE_STRING).trim()
            : (o.VALUE_NUMERIC != null && String(o.VALUE_NUMERIC).trim() !== '' ? String(o.VALUE_NUMERIC).trim() : '');
          const displayUnits = normalizeUnits(o.UNITS);
          const displayRange = normalizeRange(o.REFERENCE_RANGE);
          const { isAbnormal, badge } = classifyAbnormal(o.ABNORMAL_FLAG);
          const srcRaw = String(o.SOURCE || '').trim().toLowerCase();
          const sourceLabel = SOURCE_LABELS[srcRaw] || (srcRaw ? srcRaw.toUpperCase() : '');
          const code = String(o.CODE || '').trim();
          const codeSubtitle = code && code.toLowerCase() !== displayName.toLowerCase() ? `LOINC ${code}` : '';
          return {
            ...o,
            displayName,
            displayValue: rawVal,
            displayUnits,
            displayRange,
            isAbnormal,
            abnormalBadge: badge,
            sourceLabel,
            codeSubtitle,
          };
        })
        .filter(l => l.displayName && l.displayValue && l.displayValue !== '#LTC' && l.displayValue !== '!q')
        .sort((a, b) => (b.SERVICE_DATE || '').localeCompare(a.SERVICE_DATE || ''));
    },
    sourceOptions() {
      return [...new Set(this.labs.map(l => l.sourceLabel).filter(Boolean))].sort();
    },
    filteredLabs() {
      let list = this.labs;
      if (this.sourceFilter) {
        list = list.filter(l => l.sourceLabel === this.sourceFilter);
      }
      if (this.abnormalOnly) {
        list = list.filter(l => l.isAbnormal);
      }
      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(lab =>
          lab.displayName.toLowerCase().includes(q) ||
          (lab.CODE || '').toLowerCase().includes(q)
        );
      }
      return list;
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '—';
      return formatCalendarDate(dateString, 'en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      });
    }
  }
};
</script>
